import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import "./ProjectsView.scss";
import { timGridOffset } from "./timCompactLayout";
import { attachGridAutoHeight } from "./timGridAutoHeight";
import type { VirtualElement } from "@popperjs/core";
import DataGrid, { Column, FilterRow, Paging, Scrolling, Selection, StateStoring, Editing, LoadPanel } from "devextreme-react/data-grid";
import { useRecoilState } from 'recoil';
import { tasksGridStateAtom } from '../../recoil/store/gridState.store';
import { Autocomplete, Button, TextField, InputAdornment, IconButton, Stack, CircularProgress, Chip, Checkbox, Popper, Paper, List, ListItem, ListItemButton, ListItemText, Dialog, DialogActions, DialogContent, DialogTitle, Tooltip, ClickAwayListener } from "@mui/material";
import FilterListIcon from '@mui/icons-material/FilterList';
import FilterAltOffIcon from '@mui/icons-material/FilterAltOff';
import SearchIcon from "@mui/icons-material/Search";
import ViewColumnIcon from "@mui/icons-material/ViewColumn";
import EditIcon from "@mui/icons-material/Edit";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import ToggleOnIcon from '@mui/icons-material/ToggleOn';
import ToggleOffIcon from '@mui/icons-material/ToggleOff';
import CloseIcon from '@mui/icons-material/Close';
import { createSitesClientApi, userAssignmentClientApi } from "../../api-client-nswag/taxportal-client-runtime";
import { REACT_APP_TIMBASE_URL } from "../../envVariables";
import CellRenderDate from "../../util/cellRendersAtoms/CellRenderDate";
import { FormatDate } from "../../api-client-nswag/taxportal-client";
import EditTaskDialog, { TaskEditResult } from "./EditTaskDialog";
import { useSnackbar } from "../../hooks";
import { SnackbarMessage } from "../../hooks/useSnackbar.types";
import { calculateGridHeight } from "../../util/Utils";
import type { TimTasksFilterRequest } from "../../api-client-nswag/create-sites-client";
import { processTaskUpdateResponse, buildRowOverridesFromResult, showTaskUpdateSnackbar, formatAssigneeName } from "./taskUpdateUtils";

export type TaskRow = {
  id: string;
  // When true the parent Project is hidden and task visibility is forced off
  isHiddenProject?: boolean;
  numericTaskId?: number;
  projectIdMDM?: number;
  projectIdTim?: number;
  type?: string;
  entity?: string;
  project?: string;
  projectentity?: string;
  stage?: string;
  phase?: string;
  task?: string;
  // Canonical API-facing flag; true means the task is hidden.
  isHidden?: boolean;
  assignedTo?: string;
  // optional emails returned by the API (may be comma-separated for multiples)
  assignedEmail?: string;
  status?: string;
  nearestduedate?: string;
  lastupdated?: string;
  lastupdatedby?: string;
};

const PAGE_SIZE = 50;
// Bump when the persisted grid-state shape changes, so stale Recoil/sessionStorage
// data from a previous version is discarded instead of silently misapplied.
const GRID_STATE_SCHEMA_VERSION = 3;

const INLINE_EDIT_FIELDS = ['status', 'assignedTo'];
const ALWAYS_HIDDEN_COLUMNS = ['actions'];
// lastupdated/lastupdatedby are chooser-only columns; the chooser isn't available in client view.
const CLIENT_VIEW_HIDDEN_COLUMNS = ['type', 'stage', 'isHidden', 'lastupdated', 'lastupdatedby'];
const INTERNAL_LOCK_TOOLTIP = 'Internal task- status and assigned to cannot be edited here. Visibility is still available.';
const LOCK_ICON_SVG = '<svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true" focusable="false"><path fill="currentColor" d="M18 8h-1V6c0-2.76-2.24-5-5-5S7 3.24 7 6v2H6c-1.1 0-2 .9-2 2v10c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V10c0-1.1-.9-2-2-2zM9 6c0-1.66 1.34-3 3-3s3 1.34 3 3v2H9V6zm9 14H6V10h12v10zm-6-3c1.1 0 2-.9 2-2s-.9-2-2-2-2 .9-2 2 .9 2 2 2z"/></svg>';
const isInternalTask = (row?: { type?: string } | null) => String(row?.type || '').toLowerCase().includes('internal');

type FilterOption = string | { id: number | string; name: string };
const filterOptionText = (v: FilterOption): string => (typeof v === 'string' ? v : (v?.name ?? ''));

const hasOptionId = (o: FilterOption): boolean =>
  typeof o !== 'string' && o?.id !== null && o?.id !== undefined && o.id !== '' && Number.isFinite(Number(o.id));
const filterOptionIds = (list?: FilterOption[]): number[] =>
  (list ?? []).filter(hasOptionId).map((o) => Number((o as { id: number | string }).id));
// Options with a null id can't be told apart by id or name, so they match by reference only.
const isSameFilterOption = (a: FilterOption, b: FilterOption): boolean => {
  if (a === b) return true;
  if (typeof a === 'string' || typeof b === 'string') return false;
  return hasOptionId(a) && hasOptionId(b) && String(a.id) === String(b.id);
};
const filterOptionNames = (list?: FilterOption[]): string[] =>
  (list ?? []).map((o) => filterOptionText(o).trim()).filter(Boolean);

// Only non-empty filters are included in the request body.
const buildTasksFilterRequest = (f: any): TimTasksFilterRequest => {
  const body: TimTasksFilterRequest = {};
  const clientIds = filterOptionIds(f?.projectEntity);
  const taskEntitiesId = filterOptionIds(f?.entity);
  const projectIdMDM = filterOptionIds(f?.project);
  // Projects with an id are sent by id only; those with a null id (or free-typed) by name.
  const projectNames = filterOptionNames((f?.project ?? []).filter((o: FilterOption) => !hasOptionId(o)));
  const phaseNames = filterOptionNames(f?.stage);
  const tasksNames = filterOptionNames(f?.task);
  if (clientIds.length) body.clientIds = clientIds;
  if (taskEntitiesId.length) body.taskEntitiesId = taskEntitiesId;
  if (projectNames.length) body.projectNames = projectNames;
  if (projectIdMDM.length) body.ProjectIdsMDM = projectIdMDM;
  if (phaseNames.length) body.phaseName = phaseNames;
  if (tasksNames.length) body.TaskNames = tasksNames;
  return body;
};

type TasksGridProps = {
  isClientView?: boolean;
  isSwitchingView?: boolean;
  onCountChange?: (count: number) => void;
  onSwitchToClientView?: () => void;
  onSave?: () => void;
  isDirty?: boolean;
  savingChanges?: boolean;
  onDirtyChange?: (dirty: boolean) => void;
  onSavingChange?: (saving: boolean) => void;
  onCancelRequest?: () => void;
  cancelRef?: React.MutableRefObject<(() => void) | null>;
  onSelectionCountChange?: (count: number) => void;
  registerToolbarControls?: (controls: any) => void;
  onOpenProjectDetails?: (projectIdMDM?: number) => void;
};

const TasksGrid: React.FC<TasksGridProps> = ({ isClientView = false, isSwitchingView = false, onCountChange, onSwitchToClientView, onSave, isDirty = false, savingChanges = false, onDirtyChange, onSavingChange, onCancelRequest, cancelRef, onSelectionCountChange, registerToolbarControls, onOpenProjectDetails }) => {
  const [rows, setRows] = useState<TaskRow[]>([]);
  const [selectedRowsMap, setSelectedRowsMap] = useState<Record<string, boolean>>({});
  const [totalCount, setTotalCount] = useState<number>(0);
  const [offset, setOffset] = useState<number>(0);
  const [limit] = useState<number>(PAGE_SIZE);
  const [search, setSearch] = useState("");
  const hiddenOverridesRef = useRef<Record<string, boolean>>({});
  const statusOverridesRef = useRef<Record<string, string>>({});
  const assignedToOverridesRef = useRef<Record<string, string>>({});
  const assignedEmailOverridesRef = useRef<Record<string, string>>({});
  const rowResultRef = useRef<Record<string, 'success' | 'error'>>({});
  const [loading, setLoading] = useState<boolean>(false);
  // True from the internal/client view change until its tasks response arrives.
  const [viewSwitchLoading, setViewSwitchLoading] = useState<boolean>(false);
  const switching = isSwitchingView || viewSwitchLoading;
  const [lastFetchCount, setLastFetchCount] = useState<number>(0);
  const [tasksGridRowsViewEl, setTasksGridRowsViewEl] = useState<HTMLElement | null>(null);
  const [filterType, setFilterType] = useState<'all' | 'client' | 'internal'>('all');
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [filters, setFilters] = useState<{ projectEntity?: FilterOption[]; entity?: FilterOption[]; project?: FilterOption[]; stage?: FilterOption[]; task?: FilterOption[] }>({});
  const modalInitialFiltersRef = useRef<{ projectEntity?: FilterOption[]; entity?: FilterOption[]; project?: FilterOption[]; task?: FilterOption[] } | null>(null);
  const filtersActive = useMemo(() => Object.values(filters).some(v => Array.isArray(v) ? v.length > 0 : (v !== undefined && v !== null && String(v).trim() !== '')), [filters]);
  const [suggestionsProjectEntity, setSuggestionsProjectEntity] = useState<any[]>([]);
  const [suggestionsEntity, setSuggestionsEntity] = useState<any[]>([]);
  const [suggestionsStage, setSuggestionsStage] = useState<any[]>([]);
  const [suggestionsProject, setSuggestionsProject] = useState<any[]>([]);
  const [suggestionsTask, setSuggestionsTask] = useState<any[]>([]);
  const debounceRefs = useRef<Record<string, number | null>>({ projectEntity: null, entity: null, stage: null, project: null, task: null });
 
  const [pendingVisibilityChanges, setPendingVisibilityChanges] = useState<Record<string, boolean>>({});
  const [isSavingLocal, setIsSavingLocal] = useState(false);
  const abortRef = useRef<AbortController | null>(null);
  const initialLoadRef = useRef<boolean>(true);
  const initialTasksColumnsOrderRef = useRef<string[] | null>(null);
  const gridRef = useRef<any>(null);
  const lastTasksAppliedGridInstanceRef = useRef<any>(null);
  const lastTasksAppliedHiddenColumnsKeyRef = useRef<string | null>(null);
  const suppressTasksColumnOptionChangedRef = useRef<boolean>(false);
  const hiddenColumnsRef = useRef<string[]>([]);
  const isClientViewRef = useRef(isClientView);
  isClientViewRef.current = isClientView;
  const [tasksGridState, setTasksGridState] = useRecoilState(tasksGridStateAtom);
  const [tasksGridStateLocal] = useState(null);
  const originalHiddenRef = useRef<Record<string, boolean>>({});
  const selectedRowsMapRef = useRef<Record<string, boolean>>({});
  const [isEditTaskOpen, setIsEditTaskOpen] = useState(false);
  const [inlineEditorOpen, setInlineEditorOpen] = useState(false);
  const [lockTooltipAnchor, setLockTooltipAnchor] = useState<HTMLElement | null>(null);
  const [inlineEditorType, setInlineEditorType] = useState<'status'|'assignee'|null>(null);
  const [inlineEditorAnchorEl, setInlineEditorAnchorEl] = useState<HTMLElement | VirtualElement | null>(null);
  const [inlineEditingRow, setInlineEditingRow] = useState<TaskRow | null>(null);
  const [assignees, setAssignees] = useState<any[]>([]);
  const [loadingAssignees, setLoadingAssignees] = useState(false);
  const [inlineSaving, setInlineSaving] = useState(false);
  const [savingCells, setSavingCells] = useState<Record<string, boolean>>({});
  const [confirmInlineEdit, setConfirmInlineEdit] = useState<null | { row: TaskRow; field: string; oldValue: any; newValue: any }>(null);
  const [lastSavedEdit, setLastSavedEdit] = useState<null | { rowId: string; field: string; oldValue: any; newValue: any }>(null);
  const [inlineAssigneeValue, setInlineAssigneeValue] = useState<string>("");
  const { showSnackbar } = useSnackbar();

  const STATUS_COLOR_MAP: Record<string, { background?: string; color?: string; border?: string }> = {
    'new': { background: '#f0f0f0', color: '#666' },
    'ready to start': { background: '#fff8e1', color: '#e65100' },
    'in progress': { background: '#e3f2fd', color: '#0d47a1' },
    'on hold': { background: '#fce4ec', color: '#c2185b' },
    'done': { background: '#e8f5e9', color: '#1b5e20' },
    'n/a': { background: '#fafafa', color: '#666' },
  };

  const toggleVisibility = (id: string, currentIsHidden: boolean, buttonEl?: HTMLElement) => {
    const newIsHidden = !currentIsHidden;
    hiddenOverridesRef.current = { ...hiddenOverridesRef.current, [id]: newIsHidden };
    setPendingVisibilityChanges((prev) => {
      const originalIsHidden = originalHiddenRef.current[id];
      let next: Record<string, boolean>;
      if (newIsHidden === originalIsHidden) {
        next = { ...prev };
        delete next[id];
      } else {
        next = { ...prev, [id]: newIsHidden };
      }
      onDirtyChange?.(Object.keys(next).length > 0);
      return next;
    });
    // If we have the button element, toggle the icon display directly to avoid forcing
    // a DevExtreme repaint which can cause flicker. Otherwise fall back to repaint.
    try {
      if (buttonEl) {
        try {
          const onSpan = buttonEl.querySelector('.vis-on') as HTMLElement | null;
          const offSpan = buttonEl.querySelector('.vis-off') as HTMLElement | null;
          if (onSpan) onSpan.style.display = newIsHidden ? 'none' : '';
          if (offSpan) offSpan.style.display = newIsHidden ? '' : 'none';
        } catch {}
        return;
      }
      const instance = gridRef.current?.instance;
      const rowIndex = instance?.getRowIndexByKey(id);
      if (instance && rowIndex >= 0) {
        instance.repaintRows([rowIndex]);
      } else {
        instance?.repaint();
      }
    } catch {}
  };

  const repaintRow = (id: string) => {
    try {
      const instance = gridRef.current?.instance;
      const rowIndex = instance?.getRowIndexByKey(id);
      if (instance && rowIndex >= 0) {
        instance.repaintRows([rowIndex]);
      } else {
        instance?.repaint();
      }
    } catch {}
  };

  const markRowResults = (successIds: string[], errorIds: string[]) => {
    successIds.forEach((id) => { rowResultRef.current[id] = 'success'; });
    errorIds.forEach((id) => { rowResultRef.current[id] = 'error'; });
    requestAnimationFrame(() => [...successIds, ...errorIds].forEach((id) => repaintRow(id)));
  };

  const toggleRowSelect = (id: string) => {
    setSelectedRowsMap((prev) => {
      const next = { ...prev, [id]: !prev[id] };
      selectedRowsMapRef.current = next;

      try {
        const grid = gridRef.current?.instance;
        if (grid && typeof grid.isRowSelected === 'function' && typeof grid.selectRows === 'function') {
          const currentlySelected = grid.isRowSelected(id);
          if (!currentlySelected && next[id]) {
            grid.selectRows([id], true);
          } else if (currentlySelected && !next[id]) {
            grid.deselectRows([id]);
          }
        }
      } catch (e) {
        // ignore selection API failures
      }

      return next;
    });
    try { onSelectionCountChange?.(Object.values(selectedRowsMapRef.current).filter(Boolean).length); } catch {}
  };

  // Ensure tasks grid preserves initial column order and can restore visibleIndex
  const applyTasksGridColumnVisibility = useCallback((hidden: string[]) => {
    const inst = gridRef.current?.instance;
    if (!inst) return;
    // capture initial order once per grid instance
    if (!initialTasksColumnsOrderRef.current || lastTasksAppliedGridInstanceRef.current !== inst) {
      try {
        const cols: any[] = (typeof inst.getColumns === 'function') ? inst.getColumns() : [];
        initialTasksColumnsOrderRef.current = cols.map((c: any) => c.name || c.dataField || String(c.caption || '')).filter(Boolean);
        lastTasksAppliedGridInstanceRef.current = inst;
      } catch {}
    }

    const normalize = (s: any) => String(s || '').trim().toLowerCase();
    // Forced hiding lives here (not as JSX `visible` props) so devextreme-react's
    // controlled-prop rollback can't fight the chooser.
    // Column-chooser (`hidden`) choices are internal-only and never carried into client view.
    const effectiveHidden = isClientViewRef.current
      ? [...ALWAYS_HIDDEN_COLUMNS, ...CLIENT_VIEW_HIDDEN_COLUMNS]
      : [...(hidden || []), ...ALWAYS_HIDDEN_COLUMNS];
    const hiddenSet = new Set(effectiveHidden.map(normalize));
    const hiddenKey = [...effectiveHidden].sort().join("|");
    suppressTasksColumnOptionChangedRef.current = true;
    try {
      inst.beginUpdate?.();

      // Operate only on actual grid columns returned by the instance.
      const cols: any[] = (typeof inst.getColumns === 'function') ? inst.getColumns() : (inst.option && inst.option('columns')) || [];
      const colInfos = cols.map((c: any) => ({ col: c, key: c?.name || c?.dataField || String(c?.caption || ''), norm: normalize(c?.name || c?.dataField || String(c?.caption || '')) })).filter((x: any) => x.key);

      // Reordering is disabled, so declaration order is the canonical column order.
      const assignments: Record<string, number> = {};
      let nextIndex = 0;
      for (const ci of colInfos) {
        if (!hiddenSet.has(ci.norm)) assignments[ci.key] = nextIndex++;
      }

      // Apply visibility and visibleIndex only to existing columns.
      // Address columns by name: getColumns() includes the selection command column, so its
      // positions don't line up with columnOption's numeric indexes.
      for (const ci of colInfos) {
        const shouldBeVisible = !hiddenSet.has(ci.norm);
        try {
          if (inst.columnOption(ci.key, 'visible') !== shouldBeVisible) inst.columnOption(ci.key, 'visible', shouldBeVisible);
        } catch {}
        if (shouldBeVisible) {
          const idx = assignments[ci.key];
          try { if (typeof idx === 'number' && inst.columnOption(ci.key, 'visibleIndex') !== idx) inst.columnOption(ci.key, 'visibleIndex', idx); } catch {}
        }
      }

      lastTasksAppliedHiddenColumnsKeyRef.current = hiddenKey;
      lastTasksAppliedGridInstanceRef.current = inst;
    } finally {
      inst.endUpdate?.();
      window.setTimeout(() => { suppressTasksColumnOptionChangedRef.current = false; }, 0);
    }
  }, []);

  const allSelected = rows.length > 0 && rows.every(r => !!selectedRowsMap[r.id]);
  const someSelected = !allSelected && rows.some(r => !!selectedRowsMap[r.id]);

  const handleSelectAll = () => {
    const shouldSelectAll = !allSelected;
    const next: Record<string, boolean> = {};
    rows.forEach(r => { next[r.id] = shouldSelectAll; });
    setSelectedRowsMap(next);
    selectedRowsMapRef.current = next;
    window.requestAnimationFrame(() => gridRef.current?.instance?.repaint());
    try { onSelectionCountChange?.(Object.values(next).filter(Boolean).length); } catch {}
  };

  const getInitials = (name?: string) => {
    if (!name) return "";
    const parts = name.split(/\s+/).filter(Boolean);
    if (parts.length === 0) return "";
    if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  };

  // Engagement hover popper state (to mirror ProjectsView behavior)
  const [engagementHoverAnchorEl, setEngagementHoverAnchorEl] = useState<HTMLElement | VirtualElement | null>(null);
  const [engagementHoverManagers, setEngagementHoverManagers] = useState<any[]>([]);
  const [isEngagementHoverOpen, setIsEngagementHoverOpen] = useState(false);
  const engagementHoverCloseTimerRef = useRef<number | null>(null);
  const engagementHoverCardRef = useRef<HTMLDivElement | null>(null);
  const [engagementHoverRow, setEngagementHoverRow] = useState<TaskRow | null>(null);
  const [removingAssigneeIdx, setRemovingAssigneeIdx] = useState<number | null>(null);

  const createVirtualAnchor = useCallback((el: HTMLElement): VirtualElement => {
    const rect = el.getBoundingClientRect();
    return {
      getBoundingClientRect: () => rect,
      contextElement: el,
    } as any;
  }, []);

  const normalizeManagers = (list: any[]): any[] => {
    if (!Array.isArray(list)) return [];
    return list.map((m) => {
      if (!m) return { name: "", email: "" };
      if (typeof m === "string") {
        const s = m.trim();
        if (/@/.test(s)) {
          const namePart = s.split("@")[0];
          const name = namePart.replace(/\.|_/g, " ");
          return { name: name || s, email: s };
        }
        return { name: s, email: "" };
      }
      if (typeof m === "object") {
        return { name: m.name ?? m.displayName ?? m.fullName ?? "", email: m.email ?? m.mail ?? "", id: m.id ?? m.userId ?? null };
      }
      return { name: String(m), email: "" };
    });
  };

  const openEngagementHover = useCallback((anchorEl: HTMLElement, managers: any[], row?: TaskRow) => {
    if (engagementHoverCloseTimerRef.current !== null) {
      window.clearTimeout(engagementHoverCloseTimerRef.current);
      engagementHoverCloseTimerRef.current = null;
    }
    setEngagementHoverAnchorEl(createVirtualAnchor(anchorEl));
    setEngagementHoverManagers(normalizeManagers(managers));
    setEngagementHoverRow(row ?? null);
    setIsEngagementHoverOpen(true);
  }, [createVirtualAnchor]);

  const scheduleCloseEngagementHover = () => {
    if (engagementHoverCloseTimerRef.current !== null) {
      window.clearTimeout(engagementHoverCloseTimerRef.current);
    }
    engagementHoverCloseTimerRef.current = window.setTimeout(() => {
      setIsEngagementHoverOpen(false);
    }, 120);
  };

  const keepEngagementHoverOpen = () => {
    if (engagementHoverCloseTimerRef.current !== null) {
      window.clearTimeout(engagementHoverCloseTimerRef.current);
      engagementHoverCloseTimerRef.current = null;
    }
  };

  
  useEffect(() => {
    if (!isEngagementHoverOpen) return;

    const handleClose = () => setIsEngagementHoverOpen(false);

    const handleScroll = (e: Event) => {
      const target = e.target as Element;
      const isInsideCard = engagementHoverCardRef.current?.contains(target);
      if (!isInsideCard) {
        handleClose();
      }
    };

    const handleWheel = (e: WheelEvent) => {
      const target = e.target as Element;
      const isInsideCard = engagementHoverCardRef.current?.contains(target);
      if (!isInsideCard) {
        handleClose();
      }
    };

    window.addEventListener("scroll", handleScroll, true);
    window.addEventListener("resize", handleClose);
    window.addEventListener("wheel", handleWheel, { passive: true } as any);
    return () => {
      window.removeEventListener("scroll", handleScroll, true);
      window.removeEventListener("resize", handleClose);
      window.removeEventListener("wheel", handleWheel as any);
    };
  }, [isEngagementHoverOpen]);

  const openFilterModal = useCallback(() => {
    // snapshot current filters so we can detect changes inside modal
    modalInitialFiltersRef.current = { ...(filters || {}) };
    setIsFilterOpen(true);
  }, [filters]);

  const modalHasChanges = useMemo(() => {
    const original = modalInitialFiltersRef.current;
    if (!original) return false;
    const keys: (keyof typeof filters)[] = ['projectEntity', 'entity', 'project', 'stage', 'task'];
    const normalize = (val: any) => {
      if (Array.isArray(val)) return val.map((s: any) => filterOptionText(s).trim()).filter(Boolean).join('|');
      return String(val ?? '').trim();
    };
    for (const k of keys) {
      const a = normalize((original as any)[k]);
      const b = normalize((filters as any)[k]);
      if (a !== b) return true;
    }
    return false;
  }, [filters]);

  const getSiteId = () => {
    const raw = sessionStorage.getItem("siteId");
    if (!raw) return 0;
    // strip non-numeric characters (defensive) and parse with radix
    const cleaned = String(raw).trim().replace(/[^0-9-]/g, "");
    const n = parseInt(cleaned || "0", 10);
    if (Number.isNaN(n)) {
      return 0;
    }
    return n;
  };

  const triggerFilterSearch = (field: 'projectEntity' | 'entity' | 'project' | 'task' | 'stage', value?: string) => {
    const siteId = getSiteId() || 0; // allow 0 so lookups still happen in UI when siteId not set
    // debug: if siteId is 0, requests will use 0
    const trimmed = (value || '').trim();
    const key = field;

    // clear any existing debounce
    const existing = debounceRefs.current[key];
    if (existing) window.clearTimeout(existing as number);

    if (trimmed.length <= 2) {
      // clear suggestions if input too short
      if (key === 'projectEntity') setSuggestionsProjectEntity([]);
      if (key === 'entity') setSuggestionsEntity([]);
      if (key === 'project') setSuggestionsProject([]);
      if (key === 'stage') setSuggestionsStage([]);
      return;
    }

    // debounce 300ms
    const tid = window.setTimeout(async () => {
      try {
        if (key === 'projectEntity' || key === 'entity') {
          const resp: any = await createSitesClientApi.tim_FilterEntity(siteId, trimmed);
          const data = resp?.data ?? resp?.data?.data ?? resp;
          const list = Array.isArray(data) ? data : (data?.data ?? data ?? []);
          if (key === 'projectEntity') setSuggestionsProjectEntity(list);
          else setSuggestionsEntity(list);
        } else if (key === 'stage' || key === 'project' || key === 'task') {
          // Use unified search endpoint for stage/project/task
          const resp: any = await createSitesClientApi.tim_SearchTerm(siteId, key, trimmed);
          const data = resp?.data ?? resp?.data?.data ?? resp;
          const list = Array.isArray(data) ? data : (data?.data ?? data ?? []);
          if (key === 'stage') setSuggestionsStage(list);
          else if (key === 'project') setSuggestionsProject(list);
          else if (key === 'task') setSuggestionsTask(list);
        }
      } catch (err) {
      }
    }, 300);

    debounceRefs.current[key] = tid;
  };

  // cleanup debounce timers on unmount
  useEffect(() => {
    return () => {
      Object.values(debounceRefs.current).forEach((t) => { if (t) window.clearTimeout(t as number); });
    };
  }, []);

  const fetchTasks = async (append = false, f?: any) => {
    const siteId = getSiteId();
    if (!siteId) {
      setViewSwitchLoading(false);
      return;
    }
    if (abortRef.current) {
      abortRef.current.abort();
    }
    const controller = new AbortController();
    abortRef.current = controller;
    setLoading(true);
    try {
      const applied = f ?? filters;
      const resp: any = await createSitesClientApi.tim_Tasks(siteId, offset, limit, buildTasksFilterRequest(applied), isClientViewRef.current, controller.signal);
      const payload = resp?.data ?? resp;
      const tasksCandidate = payload?.tasks ?? payload?.data?.tasks ?? resp?.tasks ?? [];
      const totalCandidate = payload?.totalcount ?? payload?.data?.totalcount ?? resp?.totalcount ?? 0;

      const tasksArray = Array.isArray(tasksCandidate) ? tasksCandidate : [];
      const mapped = tasksArray.map((t: any) => {
        const rawTaskId = t?.taskid ?? t?.TaskId ?? t?.taskId;
        const id = String(rawTaskId ?? t?.Task ?? "");
        const isHiddenVal = t?.isHidden ?? t?.ishidden;
        const visibilityRaw = t?.Visibility;
        const resolvedIsHidden =
          typeof isHiddenVal === "boolean"
            ? isHiddenVal
            : visibilityRaw !== undefined && visibilityRaw !== null
            ? !(visibilityRaw === "Visible" || visibilityRaw === "true" || visibilityRaw === true)
            : false;
        return {
          id,
          numericTaskId: rawTaskId != null ? Number(rawTaskId) : undefined,
          projectIdMDM: t?.projectidmdm != null ? Number(t.projectidmdm) : (t?.projectIdMDM != null ? Number(t.projectIdMDM) : (t?.ProjectIdMDM != null ? Number(t.ProjectIdMDM) : undefined)),
          projectIdTim: t?.projectidtim != null ? Number(t.projectidtim) : (t?.projectIdTim != null ? Number(t.projectIdTim) : (t?.ProjectIdTim != null ? Number(t.ProjectIdTim) : undefined)),
          // API may now return a project-level hidden flag which forces task visibility off
          isHiddenProject: (t?.IsHiddenProject ?? t?.isHiddenProject ?? t?.isHiddenproject) === true,
          type: t?.type ?? "",
          entity: t?.entity ?? "",
          project: t?.project ?? "",
          projectentity: t?.projectentity ?? t?.ProjectEntity ?? "",
          stage: t?.stage ?? t?.Stage ?? "",
          phase: t?.phase ?? "",
          task: t?.Task ?? t?.task ?? "",
          assignedTo: t?.AssignedTo ?? t?.assignedTo ?? "",
          assignedEmail: t?.assignedEmail ?? t?.AssignedEmail ?? "",
          isHidden: resolvedIsHidden,
          status: t?.Status ?? t?.status ?? "",
          nearestduedate: t?.nearestduedate ?? t?.NearestDueDate ?? "",
          lastupdated: t?.lastupdated ?? t?.LastUpdated ?? "",
          lastupdatedby: t?.lastupdatedby ?? t?.LastUpdatedBy ?? "",
        } as TaskRow;
      });

      setLastFetchCount(mapped.length);
      // track original isHidden state for dirtiness comparisons
      const hiddenMap: Record<string, boolean> = {};
      mapped.forEach(r => { hiddenMap[r.id] = !!r.isHidden; });
      if (!append) originalHiddenRef.current = hiddenMap;
      else Object.assign(originalHiddenRef.current, hiddenMap);

      // Update rows and ensure totalCount is never less than the number of loaded rows.
      setRows((prev) => {
        const next = append ? [...prev, ...mapped] : mapped;
        const apiTotal = Number(totalCandidate) || 0;
        const nextTotal = Math.max(apiTotal, next.length);
        // A fresh (non-append) fetch replaces the total so filtered counts can go down; paging keeps the larger value.
        setTotalCount((prevTotal) => (append ? Math.max(prevTotal || 0, nextTotal) : nextTotal));
        try { onCountChange?.(Math.max(apiTotal, next.length) || 0); } catch {}
        return next;
      });
    } catch (err) {
      if ((err as any).name === "AbortError") return;
    } finally {
      // A superseded (aborted) request must not clear the newer request's loading state.
      if (abortRef.current === controller) {
        setLoading(false);
        setViewSwitchLoading(false);
        abortRef.current = null;
      }
      // mark that we've completed the first load attempt so loader overlay isn't shown repeatedly
      if (initialLoadRef.current) initialLoadRef.current = false;
    }
  };

  const handleLocalSave = async () => {
    const siteId = getSiteId();
    setIsSavingLocal(true);
    try {
      const projectsCustomizationData: Array<{ projectIdMDM: number; taskId: number; isHidden: boolean }> = [];
      Object.entries(pendingVisibilityChanges).forEach(([id, isHidden]) => {
        const row = rows.find((r) => r.id === id);
        if (!row) return;
        const projectId = row.projectIdMDM ?? 0;
        const taskId = row.numericTaskId ?? 0;
        projectsCustomizationData.push({ projectIdMDM: projectId, taskId, isHidden });
      });
      if (projectsCustomizationData.length > 0) {
        await createSitesClientApi.tim_UpdateTaskVisibility({
          hqSiteId: siteId,
          projectsCustomizationData,
        });
      }
      const savedChanges = pendingVisibilityChanges;
      setRows((prev) =>
        prev.map((r) =>
          Object.prototype.hasOwnProperty.call(savedChanges, r.id)
            ? { ...r, isHidden: savedChanges[r.id] }
            : r,
        ),
      );
      originalHiddenRef.current = { ...originalHiddenRef.current, ...savedChanges };
      setPendingVisibilityChanges({});
      onDirtyChange?.(false);
    } catch (err) {
    } finally {
      setIsSavingLocal(false);
    }
  };

  const handleCancel = () => {
    // revert pending visibility changes back to original fetched isHidden values
    const reverted = { ...hiddenOverridesRef.current };
    Object.keys(pendingVisibilityChanges).forEach((id) => {
      reverted[id] = originalHiddenRef.current[id];
    });
    hiddenOverridesRef.current = reverted;
    try { gridRef.current?.instance?.repaint(); } catch {}
    setPendingVisibilityChanges({});
    onDirtyChange?.(false);
    onCancelRequest?.();
  };

  useEffect(() => {
    if (cancelRef) cancelRef.current = handleCancel;
  });

  const selectedTaskRows = useMemo(
    () => rows.filter((r) => !!selectedRowsMap[r.id]),
    [rows, selectedRowsMap],
  );
  // Read by openEditTask/toolbar controls, which are only re-registered when
  // search/filters change - this ref keeps the current selection available to them
  // without needing selectedTaskRows in that effect's deps.
  const selectedTaskRowsRef = useRef<TaskRow[]>([]);
  selectedTaskRowsRef.current = selectedTaskRows;

  const handleOpenEditTask = () => {
    if (selectedTaskRowsRef.current.length === 0) {
      showSnackbar({ message: "Select at least one task to edit.", severity: "warning" });
      return;
    }
    setIsEditTaskOpen(true);
  };

  const fetchAssignees = async () => {
    const siteId = getSiteId();
    if (!siteId) return;
    setLoadingAssignees(true);
    try {
      const resp: any = await userAssignmentClientApi.userAssignment_GetUserAssignmentList(siteId || null, null, null, null);
      const list = resp?.data?.data ?? [];
      setAssignees(Array.isArray(list) ? list : []);
    } catch (err) {
      setAssignees([]);
    } finally {
      setLoadingAssignees(false);
    }
  };

  useEffect(() => {
    // fetch assignees proactively so lookup editor is ready for cell editing
    fetchAssignees();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const openInlineEditor = (row: TaskRow, type: 'status'|'assignee', anchorEl: HTMLElement) => {
    setInlineEditingRow(row);
    setInlineEditorType(type);
    // snapshot the bounding rect so the popper survives DevExtreme repainting/replacing the cell node
    setInlineEditorAnchorEl(createVirtualAnchor(anchorEl));
    setInlineEditorOpen(true);
    if (type === 'assignee') {
      if (assignees.length === 0) fetchAssignees();
      setInlineAssigneeValue('');
    }
  };

  const closeInlineEditor = () => {
    setInlineEditorOpen(false);
    setInlineEditorType(null);
    setInlineEditorAnchorEl(null);
    setInlineEditingRow(null);
    setInlineAssigneeValue('');
  };

  const handleInlineSave = async (newStatus?: string, newAssigneeEmail?: string) => {
    if (!inlineEditingRow) return;
    const siteId = getSiteId();
    if (!siteId) return;
    setInlineSaving(true);
    try {
      const chosen = newAssigneeEmail ? assignees.find((a: any) => a.userEmail === newAssigneeEmail) : undefined;
      const chosenName = formatAssigneeName(chosen);
      const requestTasks: any[] = [];
      const upd: any = { taskId: inlineEditingRow.numericTaskId ?? 0 };
      if (newStatus) upd.status = newStatus;
      if (newAssigneeEmail) {
        upd.assigned = chosenName;
        upd.assignedEmail = newAssigneeEmail;
        // include assignedEmployeeNumber from user assignment record if available
        upd.assignedEmployeeNumber = chosen?.userId  ?? undefined;
      }
      requestTasks.push(upd);
      const response = await createSitesClientApi.taxportal_UpdateTask(siteId, { tasks: requestTasks });
      const { updatedRows, failedRows, successMessages, errorMessages } = processTaskUpdateResponse(response, rows);
      for (const { row, result } of updatedRows) {
        const patch = buildRowOverridesFromResult(result);
        if (patch.status !== undefined) statusOverridesRef.current[row.id] = patch.status;
        if (patch.assignedTo !== undefined) assignedToOverridesRef.current[row.id] = patch.assignedTo;
        if (patch.assignedEmail !== undefined) assignedEmailOverridesRef.current[row.id] = patch.assignedEmail;
        repaintRow(row.id);
      }
      markRowResults(updatedRows.map(({ row }) => row.id), failedRows.map(({ row }) => row.id));
      showTaskUpdateSnackbar(showSnackbar, successMessages, errorMessages);
      closeInlineEditor();
    } catch (err) {
      showSnackbar({ message: SnackbarMessage.FAILED_MESSAGE, severity: 'error' });
    } finally {
      setInlineSaving(false);
    }
  };

  const handleRemoveAssignee = async (idx: number) => {
    const row = engagementHoverRow;
    const siteId = getSiteId();
    if (!row || !siteId) return;
    const remaining = engagementHoverManagers.filter((_, i) => i !== idx);
    const remainingNames = remaining.map((m: any) => String(m?.name || '').trim()).filter(Boolean).join('; ');
    const remainingEmails = remaining.map((m: any) => String(m?.email || '').trim()).filter(Boolean).join('; ');
    setRemovingAssigneeIdx(idx);
    try {
      const response = await createSitesClientApi.taxportal_UpdateTask(siteId, {
        tasks: [{ taskId: row.numericTaskId ?? 0, assigned: remainingNames, assignedEmail: remainingEmails , assignedEmployeeNumber: remaining.map((m: any) => m?.userId ?? '').filter(Boolean).join('; ') }],
      });
      const { updatedRows, failedRows, successMessages, errorMessages } = processTaskUpdateResponse(response, rows);
      if (updatedRows.length > 0) {
        assignedToOverridesRef.current[row.id] = remainingNames;
        assignedEmailOverridesRef.current[row.id] = remainingEmails;
        setEngagementHoverManagers(remaining);
        repaintRow(row.id);
      }
      markRowResults(updatedRows.map(({ row: r }) => r.id), failedRows.map(({ row: r }) => r.id));
      showTaskUpdateSnackbar(showSnackbar, successMessages, errorMessages);
    } catch {
      showSnackbar({ message: SnackbarMessage.FAILED_MESSAGE, severity: 'error' });
    } finally {
      setRemovingAssigneeIdx(null);
    }
  };

  // DevExtreme cell-edit handlers: intercept updates, show confirmation, then perform
  const performSaveEdit = async (row: TaskRow, field: string, newValue: any, oldValue: any) => {
    const siteId = getSiteId();
    if (!siteId) return false;
    const taskId = row.numericTaskId ?? 0;
    const payload: any = { taskId };
    if (field === 'status') payload.status = newValue;
    if (field === 'assignedTo') {
      // map email to name if available
      const chosen = assignees.find((a: any) => (a.userEmail || '') === newValue);
      if (chosen) {
        	  payload.assigned = formatAssigneeName(chosen);
        	  payload.assignedEmail = chosen.userEmail;
        	  // include assignedEmployeeNumber from user assignment record if available
        	  payload.assignedEmployeeNumber = chosen.userId ?? chosen.userId ?? undefined;
      } else {
        payload.assignedEmail = newValue;
        payload.assigned = newValue;
      }
    }

    const cellKey = `${row.id}_${field}`;
    setSavingCells((s) => ({ ...s, [cellKey]: true }));
    try {
      const response = await createSitesClientApi.taxportal_UpdateTask(siteId, { tasks: [payload] });
      const { updatedRows, failedRows, successMessages, errorMessages } = processTaskUpdateResponse(response, rows);
      for (const { row: r, result } of updatedRows) {
        const patch = buildRowOverridesFromResult(result);
        if (patch.status !== undefined) statusOverridesRef.current[r.id] = patch.status;
        if (patch.assignedTo !== undefined) assignedToOverridesRef.current[r.id] = patch.assignedTo;
        if (patch.assignedEmail !== undefined) assignedEmailOverridesRef.current[r.id] = patch.assignedEmail;
        repaintRow(r.id);
      }
      markRowResults(updatedRows.map(({ row: r }) => r.id), failedRows.map(({ row: r }) => r.id));
      showTaskUpdateSnackbar(showSnackbar, successMessages, errorMessages);
      if (updatedRows.length > 0) setLastSavedEdit({ rowId: row.id, field, oldValue, newValue });
      return errorMessages.length === 0;
    } catch (err) {
      showSnackbar({ message: SnackbarMessage.ERROR, severity: 'error' });
      return false;
    } finally {
      setSavingCells((s) => { const n = { ...s }; delete n[cellKey]; return n; });
    }
  };

  const onRowUpdating = (e: any) => {
    try {
      const key = e.key;
      const row = rows.find((r) => r.id === String(key));
      if (!row) return;
      const changedFields = Object.keys(e.newData || {});
      // only handle status/assignedTo edits here
      const fieldsToHandle = changedFields.filter((f) => f === 'status' || f === 'assignedTo');
      if (fieldsToHandle.length === 0) return;
      // cancel default update and show confirmation
      const f = fieldsToHandle[0];
      const newValue = (e.newData as any)[f];
      const oldValue = (row as any)[f];
      try {
        // Prevent default grid handling and save immediately
        e.cancel = true;
        // defer cancelEditData: calling it synchronously here re-enters DevExtreme's
        // editing controller mid-update and can shift edit focus onto unrelated columns
        window.setTimeout(() => {
          try { gridRef.current?.instance?.cancelEditData(); } catch {}
        }, 0);
        (async () => {
          await performSaveEdit(row, f, newValue, oldValue);
        })();
      } catch (err) {
       // console.error('Immediate save failed', err);
      }
      return;
    } catch (err) {
    }
  };

  const pendingClearSelectionRef = useRef(false);
  const clearGridSelection = () => {
    try {
      const inst = gridRef.current?.instance;
      if (!inst) return;
      if ((inst.getSelectedRowKeys?.() ?? []).length > 0) {
        inst.clearSelection();
        inst.option('selectedRowKeys', []);
      }
      selectedRowsMapRef.current = {};
      setSelectedRowsMap((prev) => (Object.keys(prev).length === 0 ? prev : {}));
      onSelectionCountChange?.(0);
    } catch {}
  };

  const handleTaskEditSaved = (updates: TaskEditResult[], failedIds: string[] = []) => {
    // Display overrides take precedence over row data in the cell renderer, so keep them in sync.
    updates.forEach((u) => {
      if (u.status !== undefined) statusOverridesRef.current[u.id] = u.status;
      if (u.assignedTo !== undefined) assignedToOverridesRef.current[u.id] = u.assignedTo;
      if (u.assignedEmail !== undefined) assignedEmailOverridesRef.current[u.id] = u.assignedEmail;
    });
    setRows((prev) =>
      prev.map((r) => {
        const update = updates.find((u) => u.id === r.id);
        if (!update) return r;
        return {
          ...r,
          status: update.status ?? r.status,
          assignedTo: update.assignedTo ?? r.assignedTo,
          assignedEmail: update.assignedEmail ?? r.assignedEmail,
        };
      }),
    );

    // Clear the selection after a save attempt, regardless of outcome.
    setSelectedRowsMap({});
    selectedRowsMapRef.current = {};
    try { onSelectionCountChange?.(0); } catch {}
    // The dataSource reload from setRows is async and restores selection by key, so also
    // clear once the grid finishes re-rendering (onContentReady), not just on the next frame.
    pendingClearSelectionRef.current = true;
    requestAnimationFrame(() => clearGridSelection());
    window.setTimeout(() => {
      clearGridSelection();
      pendingClearSelectionRef.current = false;
    }, 1000);

    markRowResults(updates.map((u) => u.id), failedIds);
  };

  useEffect(() => {
    fetchTasks(offset > 0);
  }, [offset]);

  const prevIsClientViewRef = useRef(isClientView);
  useEffect(() => {
    if (prevIsClientViewRef.current === isClientView) return;
    prevIsClientViewRef.current = isClientView;
    setViewSwitchLoading(true);
    if (offset > 0) setOffset(0);
    else fetchTasks(false);
  }, [isClientView]);

  const displayRows = useMemo(
    () => {
      if (switching) return [];
      if (!isClientView) return rows;
      return rows.filter((r) => {
        const effectiveHidden = typeof pendingVisibilityChanges[r.id] === "boolean" ? pendingVisibilityChanges[r.id] : !!r.isHidden;
        return !effectiveHidden;
      });
    },
    [rows, isClientView, pendingVisibilityChanges, switching],
  );

  const grid = useMemo(() => (
    <>
    <DataGrid
      ref={gridRef}
      dataSource={displayRows}
      showBorders={true}
      showColumnLines={false}
      showRowLines={false}
      onInitialized={(e: any) => attachGridAutoHeight(e.component, () => calculateGridHeight(timGridOffset(260)))}
      columnHidingEnabled={false}
      allowColumnReordering={false}
      keyExpr="id"
      noDataText={(loading || switching) && displayRows.length === 0 ? "" : displayRows.length === 0 ? "No Tasks" : ""}
      onSelectionChanged={(e: any) => {
        try {
          const rawKeys = Array.isArray(e.selectedRowKeys) ? e.selectedRowKeys : [];
          // filter out internal tasks so they cannot be selected (including Select All)
          const allowedKeys = rawKeys.filter((k: any) => {
            try {
              const id = String(k);
              const row = rows.find((r) => r.id === id);
              if (!row) return true; // allow if we don't know
              return !String(row.type || '').toLowerCase().includes('internal');
            } catch {
              return true;
            }
          });
          const internalKeys = rawKeys.filter((k: any) => !allowedKeys.includes(k));

          // programmatically deselect any internal rows that the grid attempted to select
          try {
            const comp = e.component || gridRef.current?.instance;
            if (internalKeys.length > 0 && comp && typeof comp.deselectRows === 'function') {
              comp.deselectRows(internalKeys);
            }
          } catch {}

          const map: Record<string, boolean> = {};
          allowedKeys.forEach((k: any) => { map[String(k)] = true; });
          selectedRowsMapRef.current = map;
          setSelectedRowsMap(map);
          onSelectionCountChange?.(allowedKeys.length);
        } catch {}
      }}
      onRowPrepared={(e: any) => {
        if (e.rowType !== 'data') return;
        const id = e.data?.id;
        if (!e.rowElement) return;
        // selection styling
        if (id && selectedRowsMapRef.current[id]) {
          e.rowElement.classList.add('selected-row');
        } else {
          e.rowElement.classList.remove('selected-row');
        }
        // update result styling (green saved / yellow failed), kept until page reload
        const result = id ? rowResultRef.current[id] : undefined;
        e.rowElement.classList.toggle('row-result-success', result === 'success');
        e.rowElement.classList.toggle('row-result-error', result === 'error');
        try {
          const typeVal = String(e.data?.type || '').toLowerCase();
          const isInternal = typeVal.includes('internal');
          if (isInternal) {
            e.rowElement.classList.add('internal-task-row');
          } else {
            e.rowElement.classList.remove('internal-task-row');
          }
        } catch {}
      }}
      onContentReady={(e: any) => {
        if (pendingClearSelectionRef.current) {
          pendingClearSelectionRef.current = false;
          clearGridSelection();
        }
        try {
          const root = (e?.element?.get ? e.element.get(0) : e?.element) as HTMLElement | undefined;
          const rowsView = root?.querySelector('.dx-datagrid-rowsview') as HTMLElement | null;
          if (rowsView) setTasksGridRowsViewEl((prev) => (prev === rowsView ? prev : rowsView));
        } catch {}
        try {
          const localRaw = localStorage.getItem('tasksGrid_hiddenColumns_v2');
          const sessionRaw = sessionStorage.getItem('tasksGrid_hiddenColumns_v2');
          const raw = localRaw ?? sessionRaw ?? null;
          const hidden: string[] = raw ? JSON.parse(raw) : ['lastupdated', 'lastupdatedby'];
          const changed = raw === null;
          if ((!localRaw && sessionRaw && Array.isArray(hidden)) || changed) {
            try { localStorage.setItem('tasksGrid_hiddenColumns_v2', JSON.stringify(hidden)); } catch {}
          }
          try { hiddenColumnsRef.current = hidden; } catch {}
          applyTasksGridColumnVisibility(hidden);
          try { window.setTimeout(() => applyTasksGridColumnVisibility(hiddenColumnsRef.current), 80); } catch {}
        } catch {}
      }}
      onOptionChanged={(e: any) => {
        try {
          if (!initialTasksColumnsOrderRef.current) return;
          if (suppressTasksColumnOptionChangedRef.current) return;
          if (isClientViewRef.current) return;
          const match = typeof e.fullName === 'string' ? e.fullName.match(/^columns\[(\d+)\]\.visible$/) : null;
          if (match) {
            const colIndex: number | undefined = parseInt(match[1], 10);
            const inst = e.component || gridRef.current?.instance;
            const name = colIndex !== undefined ? inst.columnOption(colIndex, 'name') || inst.columnOption(colIndex, 'dataField') : undefined;
            const visible = e.value;
            if (!name) return;
            try {
              const localRaw = localStorage.getItem('tasksGrid_hiddenColumns_v2');
              const sessionRaw = sessionStorage.getItem('tasksGrid_hiddenColumns_v2');
              const raw = localRaw ?? sessionRaw ?? null;
              const existing: string[] = raw ? JSON.parse(raw) : [];
              const colName = String(name);
              const willBeHidden = visible === false;
              const next = willBeHidden ? Array.from(new Set([...existing, colName])) : existing.filter((x) => x !== colName);
              try { localStorage.setItem('tasksGrid_hiddenColumns_v2', JSON.stringify(next)); } catch {}
              try { hiddenColumnsRef.current = next; } catch {}
            } catch {}
          }
        } catch {}
      }}
      onRowUpdating={onRowUpdating}
      onEditingStart={(e: any) => {
        if (isInternalTask(e.data) && INLINE_EDIT_FIELDS.includes(e.column?.dataField)) e.cancel = true;
      }}
      onFocusedCellChanging={(e: any) => {
        const row = e.rows?.[e.newRowIndex];
        const field = e.columns?.[e.newColumnIndex]?.dataField;
        if (row?.rowType === 'data' && isInternalTask(row.data) && INLINE_EDIT_FIELDS.includes(field)) e.isHighlighted = false;
      }}
      onCellPrepared={(e: any) => {
        if (e.rowType !== 'data' || !e.cellElement) return;
        const cellEl = e.cellElement as HTMLElement;
        const internal = isInternalTask(e.data);
        if (e.column?.command === 'select') {
          if (!internal) return;
          cellEl.classList.add('tp-internal-lock-cell');
          if (!cellEl.querySelector('.tp-internal-lock')) {
            const lock = document.createElement('span');
            lock.className = 'tp-internal-lock';
            lock.addEventListener('mouseenter', () => setLockTooltipAnchor(lock));
            lock.addEventListener('mouseleave', () => setLockTooltipAnchor(null));
            lock.setAttribute('role', 'img');
            lock.setAttribute('aria-label', INTERNAL_LOCK_TOOLTIP);
            lock.innerHTML = LOCK_ICON_SVG;
            cellEl.appendChild(lock);
          }
          return;
        }
        if (INLINE_EDIT_FIELDS.includes(e.column?.dataField)) {
          cellEl.classList.add(internal ? 'tp-readonly-cell' : 'tp-inline-editable');
        }
      }}
    >
      <StateStoring
        enabled={true}
        type="custom"
        storageKey="tasksGrid"
        customLoad={() => {
          try {
            if (tasksGridState?.__v === GRID_STATE_SCHEMA_VERSION) {
              return Promise.resolve(JSON.parse(JSON.stringify(tasksGridState.data)));
            }
            // Prefer localStorage for persisted grid state, migrate from sessionStorage if present
            const localRaw = localStorage.getItem('tasksGridState_v2');
            const sessionRaw = sessionStorage.getItem('tasksGridState_v2');
            const raw = localRaw ?? sessionRaw ?? null;
            if (raw) {
              const parsed = JSON.parse(raw);
              if (parsed?.__v === GRID_STATE_SCHEMA_VERSION) {
                if (!localRaw && sessionRaw) {
                  try { localStorage.setItem('tasksGridState_v2', JSON.stringify(parsed)); } catch {}
                }
                // Merge persisted hidden-columns into the loaded grid state so the
                // column chooser accurately reflects user choices immediately.
                try {
                  const hiddenRaw = localStorage.getItem('tasksGrid_hiddenColumns_v2') ?? sessionStorage.getItem('tasksGrid_hiddenColumns_v2') ?? null;
                  const hiddenList: string[] = hiddenRaw ? JSON.parse(hiddenRaw) : [];
                  if (parsed.data && Array.isArray(parsed.data.columns) && Array.isArray(hiddenList)) {
                    const hiddenSet = new Set(hiddenList);
                    parsed.data.columns = parsed.data.columns.map((c: any) => {
                      const name = c?.name || c?.dataField || String(c?.caption || '');
                      if (!name) return c;
                      return { ...c, visible: !hiddenSet.has(name) };
                    });
                    let idx = 0;
                    for (const col of parsed.data.columns) {
                      if (col.visible) {
                        col.visibleIndex = idx++;
                      }
                    }
                  }
                } catch {}
                return Promise.resolve(null);
              }
            }
          } catch {}
          return Promise.resolve(null);
        }}
        customSave={(state: any) => {
          try {
            const wrapped = { __v: GRID_STATE_SCHEMA_VERSION, data: state };
            setTasksGridState(wrapped);
            try { localStorage.setItem('tasksGridState_v2', JSON.stringify(wrapped)); } catch {}
          } catch {};
          return Promise.resolve();
        }}
      />
      
      <LoadPanel enabled={false} />
      <Editing mode="cell" allowUpdating={true} selectTextOnEditStart={true} startEditAction="click" useIcons={true} />
      <Selection mode="multiple" selectAllMode="page" showCheckBoxesMode="always" />
      <Paging enabled={false} />
      <Scrolling mode="standard" columnRenderingMode="standard" showScrollbar="always" />
      <FilterRow visible={true} />
      {/* No visibleIndex/visible props: devextreme-react rolls controlled column props back,
          which breaks chooser show/hide and order. Order = declaration order; see applyTasksGridColumnVisibility. */}
      <Column
        dataField="type"
        caption="Type"
        showInColumnChooser={!isClientView}
        width="7%"
        minWidth={80}
        alignment="left"
        dataType="string"
        filterOperations={["contains","notcontains"]}
        selectedFilterOperation="contains"
        allowEditing={false}
        headerCellRender={() => (
          <Tooltip title="Indicates whether the task is a Client task or an Internal task." placement="top" arrow>
            <span>Type</span>
          </Tooltip>
        )}
        cellRender={(cell) => {
          const v = cell.data?.type || "";
          const isSelected = !!selectedRowsMapRef.current[cell.data?.id];
          const key = String(v).toLowerCase();
          const typeClass = key.includes('client') ? 'type-client' : key.includes('internal') ? 'type-internal' : 'type-default';
          const selectedClass = isSelected ? 'selected' : '';
          return (
            <span className={`type-chip ${"type-chip-"+v.toLowerCase()}`}>
              <span className={`type-dot ${"type-dot-"+v.toLowerCase()}`} aria-hidden />
              <span className="type-label">{v}</span>
            </span>
          );
        }}
      />
      <Column dataField="projectentity" caption="Project Entity" width="11%" minWidth={110} alignment="left" dataType="string" filterOperations={["contains","notcontains"]} selectedFilterOperation="contains" allowEditing={false}         headerCellRender={() => (
          <Tooltip title="Project entity associated with the task." placement="top" arrow>
            <span>Project Entity</span>
          </Tooltip>
        )} />
      <Column dataField="project" caption="Project" width="9%" minWidth={100} alignment="left" dataType="string" filterOperations={["contains", "notcontains"]} selectedFilterOperation="contains" allowEditing={false}
          headerCellRender={() => (<Tooltip title="Project under which the task was created." placement="top" arrow><span>Project</span></Tooltip>)}
          cellRender={(cell) => {
            const projectName = cell.data?.project || "";
            const projectIdTim = cell.data?.projectIdTim;
            const base = (REACT_APP_TIMBASE_URL || "").trim().replace(/\/+$/, "");
            const timUrl = !isClientView && projectIdTim && base ? `${base}/projects/details/${encodeURIComponent(String(projectIdTim))}` : null;
            return (
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                {timUrl ? (
                  <a href={timUrl} target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()} style={{ color: "#0066cc", textDecoration: "underline", cursor: "pointer" }}>{projectName}</a>
                ) : (<span>{projectName}</span>)}
              </div>
            );
          }}
        />
      <Column dataField="stage" caption="Stage" showInColumnChooser={!isClientView} width="10%" minWidth={100} alignment="left" dataType="string" filterOperations={["contains","notcontains"]} selectedFilterOperation="contains" allowEditing={false}         headerCellRender={() => (
          <Tooltip title="Current project phase or stage in which the task is being performed." placement="top" arrow>
            <span>Stage</span>
          </Tooltip>
        )}/>
      <Column
        dataField="entity"
        cssClass="entity-column"
        caption="Entity"
        width="11%"
        minWidth={110}
        alignment="left"
        dataType="string"
        filterOperations={["contains","notcontains"]}
        selectedFilterOperation="contains"
        allowEditing={false}
        headerCellRender={() => (
          <Tooltip title="Entity associated with the task." placement="top" arrow>
            <span>Entity</span>
          </Tooltip>
        )}
        cellRender={(cell) => {
          const raw = cell.data?.entity ?? "";
          const lines = String(raw).split(/\r?\n/).map((s: string) => s.trim()).filter(Boolean);
          const visible = lines.slice(0, 2);
          const showMore = lines.length > 2;
          const tooltipContent = lines.join('\n');
          const textStyle: any = {
            fontSize: 13,
            color: 'rgba(0,0,0,0.87)',
            lineHeight: 1.1,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          };
          return (
            <Tooltip title={<div style={{ whiteSpace: 'pre-line' }}>{tooltipContent}</div>} placement="top" arrow>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 2, maxWidth: '100%' }}>
                <div style={{ ...textStyle }}>{visible[0] || ''}</div>
                {visible[1] && <div style={{ ...textStyle }}>{visible[1]}</div>}
                {showMore && <div style={{ ...textStyle }}>...</div>}
              </div>
            </Tooltip>
          );
        }}
      />
      <Column dataField="nearestduedate" caption="Nearest Due Date" width="8%" minWidth={95} cellRender={(e) => CellRenderDate(e.data.nearestduedate, FormatDate.MDY)} alignment="left" dataType="string" filterOperations={["contains","notcontains"]} selectedFilterOperation="contains" allowEditing={false} headerCellRender={() => (
          <Tooltip title="Closest upcoming due date associated with the task." placement="top" arrow>
            <span>Nearest Due Date</span>
          </Tooltip>
        )}/>
      <Column dataField="task" caption="Task" width="14%" minWidth={140} alignment="left" dataType="string" filterOperations={["contains","notcontains"]} selectedFilterOperation="contains" allowEditing={false}        headerCellRender={() => (
          <Tooltip title="Name of the task or activity that needs to be completed." placement="top" arrow>
            <span>Task</span>
          </Tooltip>
        )} />
      <Column
        dataField="assignedTo"
        caption="Assigned To"
        width="10%"
        minWidth={100}
        alignment="left"
        dataType="string"
        filterOperations={["contains","notcontains"]}
        selectedFilterOperation="contains"
        allowEditing={true}
                headerCellRender={() => (
          <Tooltip title="User currently assigned responsibility for the task." placement="top" arrow>
            <span>Assigned To</span>
          </Tooltip>
        )}
        lookup={{ dataSource: assignees.map((a: any) => ({ id: a.userEmail || '', name: `${(a.firstName || '')} ${(a.lastName || '')}`.trim() || (a.userEmail || '') })), valueExpr: 'id', displayExpr: 'name' }}
        cellRender={(cell) => {
        const overriddenAssignedTo = assignedToOverridesRef.current[cell.data?.id];
        const rawNames = typeof overriddenAssignedTo === 'string' ? overriddenAssignedTo : (cell.data?.assignedTo ?? "");
        const overriddenEmails = assignedEmailOverridesRef.current[cell.data?.id];
        const rawEmails = typeof overriddenEmails === 'string' ? overriddenEmails : (cell.data?.assignedEmail ?? "");
        const nameList = Array.isArray(rawNames) ? rawNames : String(rawNames).split(/;+/).map((s: string) => s.trim()).filter(Boolean);
        const emailList = Array.isArray(rawEmails) ? rawEmails : String(rawEmails).split(/;+/).map((s: string) => s.trim()).filter(Boolean);
        const managers = nameList.map((name: string, idx: number) => ({ name, email: emailList[idx] || "" }));
        const visible = managers.slice(0, 3);
        const extraCount = Math.max(0, managers.length - visible.length);
        const saving = !!savingCells[`${cell.data?.id}_assignedTo`];
        return (
          <div
            className="engagement-hover-container"
            onMouseEnter={(event) => openEngagementHover(event.currentTarget as HTMLElement, managers, cell.data)}
            onMouseLeave={scheduleCloseEngagementHover}
          >
            <div className="engagement-group">
              {visible.map((m: any, idx: number) => (
                <Chip
                  key={`${m.name}-${idx}`}
                  label={getInitials(m.name)}
                  size="small"
                  className="engagement-chip"
                />
              ))}
              {extraCount > 0 && (
                <Chip
                  label={`+${extraCount}`}
                  size="small"
                  className="engagement-extra-chip"
                />
              )}
              {saving && <CircularProgress size={16} sx={{ ml: 1 }} />}
            </div>
          </div>
        );
      }} />
            <Column
              dataField="status"
              caption="Status"
              width="9%"
              minWidth={100}
              alignment="left"
              dataType="string"
              filterOperations={["contains","notcontains"]}
              selectedFilterOperation="contains"
              allowEditing={false}
              headerCellRender={() => (
              <Tooltip title="Current status of the task." placement="top" arrow>
                <span>Status</span>
              </Tooltip>
        )}
              cellRender={(cell) => {
                const overriddenStatus = statusOverridesRef.current[cell.data?.id];
                const s = typeof overriddenStatus === 'string' ? overriddenStatus : (cell.data?.status ?? '');
                const saving = !!savingCells[`${cell.data?.id}_status`];
                const key = String(s || '').toLowerCase();
                const col = STATUS_COLOR_MAP[key] || {};
                const statusClass = String(s || '').toLowerCase().replace(/\s+/g, '-');
                const id = String(cell.data?.id ?? '');
                const readOnly = isInternalTask(cell.data);
                return (
                  <div
                    className="tp-status-trigger"
                    data-task-id={id}
                    style={{ display: 'flex', alignItems: 'center', gap: 8 }}
                    onClick={(evt) => {
                      if (readOnly) return;
                      openInlineEditor(cell.data, 'status', evt.currentTarget as HTMLElement);
                    }}
                  >
                    <Chip
                      label={s}
                      size="small"
                      variant="outlined"
                      className={`chip status ${statusClass}`}
                      sx={{ cursor: readOnly ? 'default' : 'pointer' }}
                    />
                    {saving && <CircularProgress size={14} />}
                  </div>
                );
              }}
            />
      <Column dataField="isHidden" caption="Visibility" showInColumnChooser={!isClientView} cssClass="visibility-column" width="7%" minWidth={80} alignment="center" allowFiltering={false} allowEditing={false}         headerCellRender={() => (
          <Tooltip title="Determines whether the task is visible to the client. Turn On to make the task visible to the client, or Off to hide it." placement="top" arrow>
            <span>Visibility</span>
          </Tooltip>
        )} cellRender={(cell) => {
        const id = cell.data.id;
        const rowIsHidden = typeof hiddenOverridesRef.current[id] === 'boolean' ? hiddenOverridesRef.current[id] : !!cell.data?.isHidden;
        const isHiddenProject = !!cell.data?.isHiddenProject;
        const tooltipText = isHiddenProject
          ? "Task visibility is controlled by the project`s visibility. To make this task visible, make the project visible."
          : (rowIsHidden ? 'Task is hidden from the client. Click to make it visible' : 'Task is visible to the client. Click to hide.');

        return (
          <Stack direction="row" sx={{ alignItems: 'center', justifyContent: 'center' }}>
            <Tooltip title={<span style={{ whiteSpace: 'pre-line' }}>{tooltipText}</span>} placement="top" arrow>
              <span className={isHiddenProject ? 'vis-project-locked' : undefined}>
                <IconButton
                  disableRipple
                  disableFocusRipple
                  onClick={(e) => {
                    e.stopPropagation();
                    if (isHiddenProject) return;
                    const current = typeof hiddenOverridesRef.current[id] === 'boolean' ? hiddenOverridesRef.current[id] : !!cell.data?.isHidden;
                    toggleVisibility(id, current, e.currentTarget as HTMLElement);
                  }}
                  aria-label={isHiddenProject ? 'visibility toggle disabled: project is hidden' : 'visibility toggle'}
                  size="small"
                  disabled={isHiddenProject}
                >
                  <span className="vis-icon vis-on" style={{ display: rowIsHidden ? 'none' : undefined }}>
                    <ToggleOnIcon sx={{ fontSize: 28, color: isHiddenProject ? '#bdbdbd' : '#00a3e0' }} />
                  </span>
                  <span className="vis-icon vis-off" style={{ display: rowIsHidden ? undefined : 'none' }}>
                    <ToggleOffIcon sx={{ fontSize: 28, color: isHiddenProject ? '#bdbdbd' : '#666666' }} />
                  </span>
                </IconButton>
              </span>
            </Tooltip>
          </Stack>
        );
      }} />
      <Column dataField="lastupdated" caption="Last Updated" width="8%" minWidth={95} cellRender={(e) => CellRenderDate(e.data.lastupdated, FormatDate.MDY)} defaultVisible={false} allowEditing={false}/>
      <Column dataField="lastupdatedby" caption="Last Updated By" width="9%" minWidth={110} defaultVisible={false} allowEditing={false}/>
      <Column name="actions" caption="Actions" defaultVisible={false} showInColumnChooser={false} minWidth={80} alignment="center" allowHiding={false} allowFiltering={false} allowEditing={false} cssClass="tp-actions-cell"  headerCellRender={() => (
          <Tooltip title="Available actions that can be performed on the task." placement="top" arrow>
            <span>Actions</span>
          </Tooltip>
        )} cellRender={(cell) => (
        <IconButton size="small" onClick={(e) => e.stopPropagation()}>
          <MoreVertIcon />
        </IconButton>
      )} />
    </DataGrid>
    </>
  ), [displayRows, isClientView, loading, switching]);

  // expose toolbar controls to parent if requested
  useEffect(() => {
    if (!registerToolbarControls) return;
    try {
      registerToolbarControls({
        showColumnChooser: () => gridRef.current?.instance?.showColumnChooser(),
        save: () => handleLocalSave(),
        getSearch: () => search,
        setSearch: (s: string) => setSearch(s),
        openFilter: () => openFilterModal(),
        openEditTask: () => handleOpenEditTask(),
        resetFilters: () => {
          try {
            const grid = gridRef.current?.instance;
            // detect whether grid has an active filter (filter row or other)
            let gridFilterValue: any = null;
            try {
              if (typeof grid?.getCombinedFilter === 'function') gridFilterValue = grid.getCombinedFilter();
              else if (typeof grid?.option === 'function') gridFilterValue = grid.option('filterValue');
            } catch {}
            const hasGridFilter = gridFilterValue != null && (!(Array.isArray(gridFilterValue) && gridFilterValue.length === 0));

            // if no modal filters and no grid filters, do nothing
            if (!filtersActive && !hasGridFilter) return;

            // clear our modal filters state
            setFilters({});
            // clear persisted grid state in recoil (so subsequent mounts/load won't reapply filters)
            try { setTasksGridState(null); } catch {}
            // clear filters applied to the grid
            try { applyFilters({}); } catch {}
            try { grid?.clearFilter?.(); } catch {}
            setOffset(0);
            // explicitly call fetch with empty filters so server receives none
            fetchTasks(false, {});
          } catch (err) {
          }
        },
        applyFilters: (f: any) => { setFilters(f || {}); applyFilters(f || {}); setOffset(0); fetchTasks(false, f || {}); },
        getFilters: () => {
          try {
            const grid = gridRef.current?.instance;
            let gridFilterValue: any = null;
            try {
              if (typeof grid?.getCombinedFilter === 'function') gridFilterValue = grid.getCombinedFilter();
              else if (typeof grid?.option === 'function') gridFilterValue = grid.option('filterValue');
            } catch {}
            const hasGridFilter = gridFilterValue != null && (!(Array.isArray(gridFilterValue) && gridFilterValue.length === 0));
            if (filtersActive) return filters;
            if (hasGridFilter) return { _gridFilter: true };
            return {};
          } catch (err) {
            return filters;
          }
        },
      });
    } catch (e) { }
    // include filtersActive so resetFilters behavior is updated when it changes
  }, [registerToolbarControls, gridRef, search, filtersActive, filters]);

  // When parent toggles client view, re-apply stored tasks hidden columns so chooser works
  useEffect(() => {
    try {
      const localRaw = localStorage.getItem('tasksGrid_hiddenColumns_v2');
      const sessionRaw = sessionStorage.getItem('tasksGrid_hiddenColumns_v2');
      const raw = localRaw ?? sessionRaw ?? null;
      const hidden: string[] = raw ? JSON.parse(raw) : [];
      if (!localRaw && sessionRaw && Array.isArray(hidden)) {
        try { localStorage.setItem('tasksGrid_hiddenColumns_v2', JSON.stringify(hidden)); } catch {}
      }
      try { hiddenColumnsRef.current = hidden; } catch {}
      suppressTasksColumnOptionChangedRef.current = true;
      applyTasksGridColumnVisibility(hidden);
      window.setTimeout(() => { suppressTasksColumnOptionChangedRef.current = false; }, 0);
    } catch {}
  }, [isClientView, applyTasksGridColumnVisibility]);

  // Apply filters locally to the grid (client-side). Defensive: no-op if grid not ready.
  const applyFilters = (f: any) => {
    try {
      const grid = gridRef.current?.instance;
      if (!grid) return;
      const buildFieldExpr = (fieldName: string, vals: any) => {
        if (!vals) return null;
        const arr = Array.isArray(vals) ? vals : [vals];
        const cleaned = arr.map((s: any) => filterOptionText(s).trim()).filter(Boolean);
        if (cleaned.length === 0) return null;
        const exprs = cleaned.map((v) => [fieldName, 'contains', v]);
        if (exprs.length === 1) return exprs[0];
        let combined: any = exprs[0];
        for (let i = 1; i < exprs.length; i++) combined = [combined, 'or', exprs[i]];
        return combined;
      };

      const exprs: any[] = [];
      const pe = buildFieldExpr('projectentity', (f as any)?.projectEntity);
      const en = buildFieldExpr('entity', (f as any)?.entity);
      const pr = buildFieldExpr('project', (f as any)?.project);
      const ta = buildFieldExpr('task', (f as any)?.task);
      const stg = buildFieldExpr('stage', (f as any)?.stage);
      if (pe) exprs.push(pe);
      if (en) exprs.push(en);
      if (pr) exprs.push(pr);
      if (ta) exprs.push(ta);
      if (stg) exprs.push(stg);

      if (exprs.length === 0) {
        grid.clearFilter?.();
      } else {
        let combined: any = exprs[0];
        for (let i = 1; i < exprs.length; i++) combined = [combined, 'and', exprs[i]];
        grid.filter(combined);
      }
    } catch (err) {
    }
  };

    // detect whether the DevExtreme grid currently has filters applied (from filter row or otherwise)
    const gridHasActiveFilter = (): boolean => {
      try {
        const grid = gridRef.current?.instance;
        if (!grid) return false;
        let gridFilterValue: any = null;
        if (typeof grid.getCombinedFilter === 'function') gridFilterValue = grid.getCombinedFilter();
        else if (typeof grid.option === 'function') gridFilterValue = grid.option('filterValue');
        return gridFilterValue != null && (!(Array.isArray(gridFilterValue) && gridFilterValue.length === 0));
      } catch (err) {
        return false;
      }
    };

  const modalFiltersActive = filtersActive; // only modal Apply should trigger highlight

  return (
    <div className="tasks-grid">
      {/* Toolbar outside of the grid so buttons are not rendered inside DevExtreme header */}
      { !registerToolbarControls && (
        <div className="tasks-toolbar-wrapper" style={{ marginBottom: 8 }}>
          <div className="tasks-toolbar" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div className="tasks-filters">
              <div className="tasks-selection-count">{Object.values(selectedRowsMap).filter(Boolean).length} selected</div>
            </div>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <span className="tasks-selection-count-inline">{Object.values(selectedRowsMap).filter(Boolean).length} selected</span>
              <Button variant="outlined" size="small" startIcon={<EditIcon sx={{ color: '#515356' }} />} endIcon={<KeyboardArrowDownIcon sx={{ color: '#515356' }} />} className="btn-grey" onClick={handleOpenEditTask}>Edit</Button>
              {!isClientView && (
              <Tooltip title="Columns" placement="bottom" arrow>
                <span>
                  <Button variant="outlined" startIcon={<ViewColumnIcon sx={{ color: '#515356', fontSize: 'small' }} />} onClick={() => gridRef.current?.instance?.showColumnChooser()} className="btn btn-grey" aria-label="Columns">Columns</Button>
                </span>
              </Tooltip>
              )}
              <Tooltip title="Filter" placement="bottom" arrow>
                <span>
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<FilterListIcon sx={{ ...(modalFiltersActive ? { color: 'primary.main' } : { color: '#515356' }), fontSize: 'small' }} />}
                    onClick={() => openFilterModal()}
                    className="btn btn-grey"
                    aria-label="Filter"
                    sx={modalFiltersActive ? { borderColor: 'primary.main', color: 'primary.main' } : {}}
                  />
                </span>
              </Tooltip>
              <Tooltip title="Reset Filters" placement="bottom" arrow>
                  <span>
                    <Button
                      variant="outlined"
                      startIcon={<FilterAltOffIcon sx={{ color: (filtersActive || gridHasActiveFilter()) ? '#8a5a00' : '#515356', fontSize: 'small' }} />}
                      onClick={() => { setFilters({}); try { setTasksGridState(null); } catch {} applyFilters({}); setOffset(0); fetchTasks(false, {}); }}
                      className={`btn btn-grey ${filtersActive ? 'btn-filter-reset-active' : ''}`}
                      aria-label="Reset Filters"
                      disabled={!filtersActive && !gridHasActiveFilter()}
                    >
                    </Button>
                  </span>
              </Tooltip>
              <TextField
                size="small"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search..."
                className="search-input"
                InputProps={{ startAdornment: (<InputAdornment position="start"><SearchIcon sx={{ color: '#757575' }} /></InputAdornment>) }}
              />
              {!isClientView && (
                <Button
                  variant="contained"
                  size="small"
                  onClick={handleLocalSave}
                  disabled={Object.keys(pendingVisibilityChanges).length === 0 || isSavingLocal}
                  sx={{ textTransform: 'none', fontSize: '14px' }}
                >
                  {isSavingLocal ? 'Saving...' : 'Save Changes'}
                </Button>
              )}
            </div>
          </div>
        </div>
      )}

      <div className="tasks-grid-wrapper nested-grid-card">
        {grid}

        {/* Shared MUI tooltip for the lock icons injected into DevExtreme selection cells */}
        <Tooltip
          title={INTERNAL_LOCK_TOOLTIP}
          placement="top-start"
          arrow
          open={!!lockTooltipAnchor && lockTooltipAnchor.isConnected}
          PopperProps={{ anchorEl: lockTooltipAnchor }}
        >
          <span style={{ display: 'none' }} />
        </Tooltip>

        <EditTaskDialog
          open={isEditTaskOpen}
          onClose={() => setIsEditTaskOpen(false)}
          siteId={getSiteId()}
          tasks={selectedTaskRows}
          onSaved={handleTaskEditSaved}
        />

        {/* Filter modal (Tasks) */}
        <Dialog open={isFilterOpen} onClose={() => setIsFilterOpen(false)} fullWidth maxWidth="md" className="tasks-filter-dialog">
          <DialogTitle>Filter Settings - Tasks</DialogTitle>
          <DialogContent>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 16, marginTop: 8 }}>
            <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div className="filter-field-label">Project Entity</div>
                <Autocomplete
                  multiple
                  freeSolo
                  disableCloseOnSelect
                  options={suggestionsProjectEntity || []}
                  filterOptions={(x) => x}
                  getOptionLabel={(option: FilterOption) => filterOptionText(option)}
                  isOptionEqualToValue={isSameFilterOption}
                  value={filters.projectEntity || []}
                  onChange={(e, newValue: any[]) => { setFilters(prev => ({ ...prev, projectEntity: newValue })); }}
                  onInputChange={(e, inputValue, reason) => { if (reason !== 'reset') triggerFilterSearch('projectEntity', inputValue); }}
                  renderOption={(props, option, { selected, index }: any) => (
                    <li {...props} key={`${filterOptionText(option)}-${index}`}>
                      <Checkbox checked={selected} size="small" sx={{ mr: 1 }} />
                      {filterOptionText(option)}
                    </li>
                  )}
                  renderTags={(value: FilterOption[], getTagProps) => (
                    <div className="filter-selected-tags">
                      {value.map((option, index: number) => (
                        <Chip {...getTagProps({ index })} key={`${filterOptionText(option)}-${index}`} label={filterOptionText(option)} size="small" />
                      ))}
                    </div>
                  )}
                  renderInput={(params) => (
                    <TextField {...params} placeholder="Select or search for projects" size="small" fullWidth />
                  )}
                />
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div className="filter-field-label">Entity</div>
                <Autocomplete
                  multiple
                  freeSolo
                  disableCloseOnSelect
                  options={suggestionsEntity || []}
                  filterOptions={(x) => x}
                  getOptionLabel={(option: FilterOption) => filterOptionText(option)}
                  isOptionEqualToValue={isSameFilterOption}
                  value={filters.entity || []}
                  onChange={(e, newValue: any[]) => { setFilters(prev => ({ ...prev, entity: newValue })); }}
                  onInputChange={(e, inputValue, reason) => { if (reason !== 'reset') triggerFilterSearch('entity', inputValue); }}
                  renderOption={(props, option, { selected, index }: any) => (
                    <li {...props} key={`${filterOptionText(option)}-${index}`}>
                      <Checkbox checked={selected} size="small" sx={{ mr: 1 }} />
                      {filterOptionText(option)}
                    </li>
                  )}
                  renderTags={(value: FilterOption[], getTagProps) => (
                    <div className="filter-selected-tags">
                      {value.map((option, index: number) => (
                        <Chip {...getTagProps({ index })} key={`${filterOptionText(option)}-${index}`} label={filterOptionText(option)} size="small" />
                      ))}
                    </div>
                  )}
                  renderInput={(params) => (
                    <TextField {...params} placeholder="Select or search for entity" size="small" fullWidth />
                  )}
                />
              </div>
            </div>
            <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div className="filter-field-label">Project</div>
                <Autocomplete
                  multiple
                  freeSolo
                  disableCloseOnSelect
                  options={suggestionsProject || []}
                  filterOptions={(x) => x}
                  getOptionLabel={(option: FilterOption) => filterOptionText(option)}
                  isOptionEqualToValue={isSameFilterOption}
                  value={filters.project || []}
                  onChange={(e, newValue: any[]) => { setFilters(prev => ({ ...prev, project: newValue })); }}
                  onInputChange={(e, inputValue, reason) => { if (reason !== 'reset') triggerFilterSearch('project', inputValue); }}
                  renderOption={(props, option, { selected, index }: any) => (
                    <li {...props} key={`${filterOptionText(option)}-${index}`}>
                      <Checkbox checked={selected} size="small" sx={{ mr: 1 }} />
                      {filterOptionText(option)}
                    </li>
                  )}
                  renderTags={(value: FilterOption[], getTagProps) => (
                    <div className="filter-selected-tags">
                      {value.map((option, index: number) => (
                        <Chip {...getTagProps({ index })} key={`${filterOptionText(option)}-${index}`} label={filterOptionText(option)} size="small" />
                      ))}
                    </div>
                  )}
                  renderInput={(params) => (
                    <TextField {...params} placeholder="Select or search for project" size="small" fullWidth />
                  )}
                />
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div className="filter-field-label">Stage</div>
                <Autocomplete
                  multiple
                  freeSolo
                  disableCloseOnSelect
                  options={suggestionsStage || []}
                  filterOptions={(x) => x}
                  getOptionLabel={(option: FilterOption) => filterOptionText(option)}
                  isOptionEqualToValue={isSameFilterOption}
                  value={filters.stage || []}
                  onChange={(e, newValue: any[]) => { setFilters(prev => ({ ...prev, stage: newValue })); }}
                  onInputChange={(e, inputValue, reason) => { if (reason !== 'reset') triggerFilterSearch('stage', inputValue); }}
                  renderOption={(props, option, { selected, index }: any) => (
                    <li {...props} key={`${filterOptionText(option)}-${index}`}>
                      <Checkbox checked={selected} size="small" sx={{ mr: 1 }} />
                      {filterOptionText(option)}
                    </li>
                  )}
                  renderTags={(value: FilterOption[], getTagProps) => (
                    <div className="filter-selected-tags">
                      {value.map((option, index: number) => (
                        <Chip {...getTagProps({ index })} key={`${filterOptionText(option)}-${index}`} label={filterOptionText(option)} size="small" />
                      ))}
                    </div>
                  )}
                  renderInput={(params) => (
                    <TextField {...params} placeholder="Select or search for stage" size="small" fullWidth />
                  )}
                />
              </div>
            </div>
            <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div className="filter-field-label">Task</div>
                <Autocomplete
                  multiple
                  freeSolo
                  disableCloseOnSelect
                  options={suggestionsTask || []}
                  filterOptions={(x) => x}
                  getOptionLabel={(option: FilterOption) => filterOptionText(option)}
                  isOptionEqualToValue={isSameFilterOption}
                  value={filters.task || []}
                  onChange={(e, newValue: any[]) => { setFilters(prev => ({ ...prev, task: newValue })); }}
                  onInputChange={(e, inputValue, reason) => { if (reason !== 'reset') triggerFilterSearch('task', inputValue); }}
                  renderOption={(props, option, { selected, index }: any) => (
                    <li {...props} key={`${filterOptionText(option)}-${index}`}>
                      <Checkbox checked={selected} size="small" sx={{ mr: 1 }} />
                      {filterOptionText(option)}
                    </li>
                  )}
                  renderTags={(value: FilterOption[], getTagProps) => (
                    <div className="filter-selected-tags">
                      {value.map((option, index: number) => (
                        <Chip {...getTagProps({ index })} key={`${filterOptionText(option)}-${index}`} label={filterOptionText(option)} size="small" />
                      ))}
                    </div>
                  )}
                  renderInput={(params) => (
                    <TextField {...params} placeholder="Select or search for task" size="small" fullWidth />
                  )}
                />
              </div>
              <div style={{ flex: 1, minWidth: 0 }} />
            </div>
            </div>
          </DialogContent>
          <DialogActions>
            <Button disabled={!modalHasChanges} onClick={() => { setFilters({}); setSuggestionsProjectEntity([]); setSuggestionsEntity([]); setSuggestionsProject([]); setSuggestionsStage([]); setSuggestionsTask([]); }}>Reset Filters</Button>
            <Button onClick={() => setIsFilterOpen(false)}>Cancel</Button>
            <Button variant="contained" disabled={!modalHasChanges} onClick={() => { applyFilters(filters); setIsFilterOpen(false); setOffset(0); fetchTasks(false, filters); }}>Apply Filters</Button>
          </DialogActions>
        </Dialog>

        {(loading && rows.length === 0 && initialLoadRef.current) || switching ? (
          <div className="tasks-grid-inline-overlay">
            <div className="tasks-grid-loading-card">
              <CircularProgress size={16} sx={{ color: "#00a3e0", mr: 1 }} />
              <span>Loading tasks...</span>
            </div>
          </div>
        ) : null}

        <Popper
          open={isEngagementHoverOpen && !!engagementHoverAnchorEl}
          anchorEl={engagementHoverAnchorEl}
          placement="bottom-start"
          className="engagement-hover-popper"
          modifiers={[
            { name: "offset", options: { offset: [0, 8] } },
            { name: "preventOverflow", options: { padding: 8 } },
            { name: "flip", options: { padding: 8 } },
          ]}
        >
          <div
            ref={engagementHoverCardRef}
            className="engagement-hover-card"
            onMouseEnter={keepEngagementHoverOpen}
            onMouseLeave={scheduleCloseEngagementHover}
            style={{ zIndex: 3000 }}
          >
            <div className="engagement-hover-list">
              {engagementHoverManagers.map((manager, idx) => (
                <div
                  className="engagement-hover-item"
                  key={`${manager?.id ?? manager?.name ?? idx}-${idx}`}
                >
                  <span className="engagement-hover-avatar">{getInitials(manager?.name || "")}</span>
                  <div className="engagement-hover-text" style={{ flex: 1, minWidth: 0 }}>
                    <div className="engagement-hover-name">{manager?.name || "-"}</div>
                    <div
                      className="engagement-hover-email engagement-email-copy"
                      title={manager?.email ? "Click to copy" : ""}
                      onClick={(event) => {
                        event.preventDefault();
                        event.stopPropagation();
                        const email = manager?.email || "";
                        if (!email) return;
                        try {
                          navigator.clipboard?.writeText?.(email);
                        } catch {
                          const input = document.createElement("input");
                          input.type = "text";
                          input.value = email;
                          input.style.position = "fixed";
                          input.style.opacity = "0";
                          document.body.appendChild(input);
                          input.focus({ preventScroll: true } as any);
                          input.select();
                          try { document.execCommand("copy"); } catch {} finally { input.remove(); }
                        }
                      }}
                    >
                      {manager?.email || ""}
                    </div>
                  </div>
                  {!isClientView && engagementHoverRow && !isInternalTask(engagementHoverRow) && (
                    <Tooltip title="Remove assignee" placement="top" arrow>
                      <span>
                        <IconButton
                          size="small"
                          aria-label={`Remove ${manager?.name || 'assignee'}`}
                          disabled={removingAssigneeIdx !== null}
                          onClick={(event) => { event.stopPropagation(); handleRemoveAssignee(idx); }}
                        >
                          {removingAssigneeIdx === idx ? <CircularProgress size={14} /> : <CloseIcon sx={{ fontSize: 16 }} />}
                        </IconButton>
                      </span>
                    </Tooltip>
                  )}
                </div>
              ))}
            </div>
            <div className="engagement-hover-view-all" onClick={() => { setIsEngagementHoverOpen(false); /* optional: open full dialog */ }}>View All</div>
          </div>
        </Popper>

        {/* Inline editor Popper for Status / Assignee */}
        <Popper
          open={inlineEditorOpen && !!inlineEditorAnchorEl}
          anchorEl={inlineEditorAnchorEl}
          placement="bottom-start"
          container={document.body}
          style={{ zIndex: 4000 }}
          modifiers={[ { name: 'offset', options: { offset: [0, 8] } }, { name: 'preventOverflow', options: { padding: 8 } } ]}
        >
          <ClickAwayListener onClickAway={(event) => {
            // A click on another status cell re-targets the editor via its own onClick; don't close it here.
            if ((event.target as Element | null)?.closest?.('.tp-status-trigger')) return;
            if (inlineEditorOpen) { setInlineEditorOpen(false); setInlineEditorType(null); setInlineEditorAnchorEl(null); setInlineEditingRow(null); } }}>
            <Paper elevation={6} sx={{ p: 1, minWidth: 220 }}>
            {inlineEditorType === 'status' && (
              <List dense>
                {TASK_STATUS_OPTIONS.map((opt: string) => {
                  const statusClass = String(opt || '').toLowerCase().replace(/\s+/g, '-');
                  const currentStatus = inlineEditingRow?.status ?? '';
                  const isSelected = String(currentStatus || '').toLowerCase() === String(opt || '').toLowerCase();
                  return (
                    <ListItemButton
                      key={opt}
                      onClick={() => handleInlineSave(opt, undefined)}
                      disabled={inlineSaving}
                      selected={isSelected}
                    >
                      <Chip
                        label={opt}
                        size="small"
                        variant={isSelected ? 'filled' : 'outlined'}
                        className={`chip status ${statusClass}`}
                        sx={{ width: '100%', justifyContent: 'flex-start', cursor: 'pointer' }}
                      />
                    </ListItemButton>
                  );
                })}
              </List>
            )}
            {inlineEditorType === 'assignee' && (
              <div style={{ display: 'flex', gap: 8, alignItems: 'center', minWidth: 320 }}>
                <Autocomplete
                  options={assignees.map((a: any) => a.userEmail || '')}
                  value={inlineAssigneeValue}
                  onChange={(_, value) => setInlineAssigneeValue(value || '')}
                  getOptionLabel={(v: any) => {
                    const a = assignees.find((x: any) => (x.userEmail || '') === v);
                    if (!a) return v || '';
                    const name = `${a.firstName || ''} ${a.lastName || ''}`.trim();
                    return name ? `${name} (${a.userEmail || ''})` : (a.userEmail || '');
                  }}
                  freeSolo
                  fullWidth
                  size="small"
                  loading={loadingAssignees}
                  renderInput={(params) => <TextField {...params} placeholder="Select assignee" />}
                />
                <Button size="small" onClick={() => closeInlineEditor()} disabled={inlineSaving}>Cancel</Button>
                <Button size="small" variant="contained" onClick={() => {
                  const val = inlineAssigneeValue || '';
                  if (!val) { showSnackbar({ message: 'Select an assignee', severity: 'warning' }); return; }
                  handleInlineSave(undefined, val);
                }} disabled={inlineSaving}>Save</Button>
              </div>
            )}
          </Paper>
          </ClickAwayListener>
        </Popper>

        {/* Confirmation dialog for cell edits */}
        <Dialog open={!!confirmInlineEdit} onClose={() => setConfirmInlineEdit(null)} fullWidth maxWidth="xs">
          <DialogTitle>Confirm Change</DialogTitle>
          <DialogContent>
            {confirmInlineEdit ? (
              <div>
                <div>Are you sure you want to change <b>{confirmInlineEdit.field}</b> for task: <b>{confirmInlineEdit.row.task}</b>?</div>
                <div style={{ marginTop: 8 }}>
                  <div><b>Current:</b> {String(confirmInlineEdit.oldValue ?? '')}</div>
                  <div><b>New:</b> {String(confirmInlineEdit.newValue ?? '')}</div>
                </div>
              </div>
            ) : null}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => {
              try {
                const inst = gridRef.current?.instance;
                if (inst && typeof inst.cancelEditData === 'function') inst.cancelEditData();
              } catch {}
              // revert any optimistic change in rows
              try {
                if (confirmInlineEdit) {
                  const { row, field, oldValue } = confirmInlineEdit;
                  setRows((prev) => prev.map((r) => (r.id === row.id ? ({ ...r, [field]: oldValue }) : r)));
                }
              } catch {}
              setConfirmInlineEdit(null);
              // also close any inline popper editor
              closeInlineEditor();
            }}>Cancel</Button>
            <Button variant="contained" onClick={async () => {
              if (!confirmInlineEdit) return;
              const { row, field, newValue, oldValue } = confirmInlineEdit;
              setConfirmInlineEdit(null);
              await performSaveEdit(row, field, newValue, oldValue);
            }}>Confirm</Button>
          </DialogActions>
        </Dialog>

      </div>

      <div className="projects-pagination">       
        <div className="pagination-right">
          <Button
            variant="text"
            className="load-more-link"
            onClick={() => setOffset((prev) => prev + limit)}
            disabled={
              loading ||
              switching ||
              // Server may omit hidden tasks from a page, so a short page alone doesn't mean the end.
              (lastFetchCount < PAGE_SIZE && offset + limit >= totalCount)
            }
          >
            {!loading && `Load the next ${PAGE_SIZE} records`}
          </Button>
        </div>
      </div>
      {
        !loading && !switching && (
          <div className="loaded-projects-bottom">
        {(() => {
          const effectiveHiddenSet = new Set<string>();
          if (isClientView) {
            rows.forEach((row) => {
              const id = row.id;
              const overridden = hiddenOverridesRef.current[id];
              const isHidden = typeof overridden === 'boolean' ? overridden : !!row.isHidden;
              if (isHidden) effectiveHiddenSet.add(id);
            });
          }

          const loadedCountForLabel = isClientView
            ? rows.reduce((count, row) => {
                const id = row.id;
                const overridden = hiddenOverridesRef.current[id];
                const isHidden = typeof overridden === 'boolean' ? overridden : !!row.isHidden;
                return isHidden ? count : count + 1;
              }, 0)
            : rows.length;

          const displayTotalBase = totalCount > 0 ? totalCount : rows.length;
          const displayTotal = isClientView ? Math.max(0, displayTotalBase - effectiveHiddenSet.size) : displayTotalBase;

          const displayStart = loadedCountForLabel > 0 ? 1 : 0;
          const displayEnd = Math.min(loadedCountForLabel, displayTotal);

          return (
            <>
              Loaded tasks: <b>{displayStart}-{displayEnd}</b> of <b>{displayTotal}</b> matching records.
            </>
          );
        })()}
      </div>
        )
      }
    </div>
  );
};

// Local copy to avoid circular import / resolution issues
const TASK_STATUS_OPTIONS: string[] = ["New", "Ready to Start", "In Progress", "On Hold", "Done", "N/A"];

export default TasksGrid;
